/**
 * Yangiliklar (ЧТЗ 4.2.11.1, INT-19) — bosh sahifadagi blok, «Barcha yangiliklar»
 * ro'yxati va maqola sahifasi uchun umumiy kartochka va yordamchilar.
 *
 * Manba: CMS yangiliklari (`NewsItem` — gov.uz/urmon integratsiyasi ustuvor,
 * qo'lda kiritilganlari zaxira). Bazada yangilik bo'lmasa bosh sahifada
 * `landing.ts` dagi namunaviy xabarlar «Namuna» belgisi bilan ko'rsatiladi.
 */
import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";

import type { PortalNewsItem } from "@/shared/api/types";
import { carouselFallback } from "@/shared/data/landing";
import { usePortalHome } from "@/shared/data/portal";
import { useI18n } from "@/shared/i18n";
import { formatDate } from "@/shared/lib/format";
import { SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { Icon } from "@/shared/ui/Icon";

import { stagger } from "./helpers";

export interface NewsCardItem {
  id: string;
  category: string;
  categoryLabel: string;
  title: string;
  summary: string;
  image: string;
  date: string;
  /** Ekotadbir o'tkaziladigan sana (e'lon sanasidan alohida). */
  eventDate: string;
  sample: boolean;
  /** Maqola sahifasi; namunada — yo'q. */
  href: string;
}

export function newsHref(id: number | string): string {
  return `${SECTION_PATHS.news}/${id}`;
}

export function toCard(item: PortalNewsItem, t: (key: string) => string): NewsCardItem {
  const category = item.category ?? "news";
  return {
    id: String(item.id),
    category,
    categoryLabel: t(`news.kind.${category}`),
    title: item.title,
    summary: item.summary,
    image: item.cover_link,
    date: item.published_at,
    eventDate: item.event_starts_at ?? "",
    sample: Boolean(item.is_sample),
    href: newsHref(item.id),
  };
}

const SAMPLE_CATEGORY: Record<string, string> = { event: "event", news: "news" };

/**
 * Bosh sahifa uchun so'nggi yangiliklar. `loading` — bosh sahifa javobi hali
 * kelmagan; `sample` — bazada yangilik yo'q, namunalar ko'rsatilmoqda.
 */
export function useHomeNews(count: number): {
  items: NewsCardItem[];
  loading: boolean;
  sample: boolean;
  live: boolean;
} {
  const { t } = useI18n();
  const home = usePortalHome();
  /* Server javob bermasa skelet abadiy qolmasin — 6 soniyadan so'ng namunalar ko'rsatiladi. */
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (home) return undefined;
    const timer = window.setTimeout(() => setGaveUp(true), 6000);
    return () => window.clearTimeout(timer);
  }, [home]);
  if (!home && !gaveUp) return { items: [], loading: true, sample: false, live: false };
  const live = Boolean(home?.news_feed?.live);
  if (home?.news?.length) {
    return { items: home.news.slice(0, count).map((item) => toCard(item, t)), loading: false, sample: false, live };
  }
  const items = carouselFallback.slice(0, count).map((slide) => {
    const category = SAMPLE_CATEGORY[slide.kind] ?? "news";
    return {
      id: slide.id,
      category,
      categoryLabel: t(`news.kind.${category}`),
      title: slide.title,
      summary: slide.text,
      image: slide.image,
      date: slide.date,
      eventDate: "",
      sample: true,
      href: "",
    };
  });
  return { items, loading: false, sample: true, live };
}

/** Yangilik kartochkasi: surat, toifa, sana, sarlavha, qisqa matn va «Batafsil». */
export function NewsCard({ item, index }: { item: NewsCardItem; index: number }) {
  const { t, locale } = useI18n();
  const style = item.image ? ({ "--cover": `url("${item.image}")` } as CSSProperties) : undefined;

  return (
    <article className={`news-tile${item.href ? " is-linked" : ""}`} data-reveal style={stagger(index % 4)}>
      <div className={`news-pic${item.image ? " has-image" : ""}`} style={style}>
        {item.image ? null : <Icon name="newspaper" size={44} strokeWidth={1.3} />}
        <span className="news-badges">
          {item.categoryLabel ? <span className={`badge news-cat is-${item.category}`}>{item.categoryLabel}</span> : null}
          {item.sample ? <span className="badge news-sample">{t("news.sample")}</span> : null}
        </span>
      </div>
      <div className="news-copy">
        <div className="news-dates">
          {item.date ? (
            <time dateTime={item.date}>
              <Icon name="calendar" size={14} />
              {formatDate(item.date, locale)}
            </time>
          ) : null}
          {item.eventDate ? (
            <span className="news-event-date">
              <Icon name="clock" size={14} />
              {t("news.eventOn")}: {formatDate(item.eventDate, locale)}
            </span>
          ) : null}
        </div>
        <h3 title={item.title}>
          {item.href ? (
            <Link to={item.href} className="news-title-link">
              {item.title}
            </Link>
          ) : (
            item.title
          )}
        </h3>
        {item.summary ? <p>{item.summary}</p> : null}
        {item.href ? (
          <span className="news-link" aria-hidden="true">
            {t("news.read")}
            <Icon name="arrowRight" size={14} />
          </span>
        ) : null}
      </div>
    </article>
  );
}

/** Yuklanish paytidagi bo'sh kartochkalar (sahifa bo'sh qolmaydi). */
export function NewsSkeleton({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div className="news-tile news-skeleton" key={index} aria-hidden="true">
          <div className="news-pic" />
          <div className="news-copy">
            <span className="sk-line is-short" />
            <span className="sk-line" />
            <span className="sk-line" />
          </div>
        </div>
      ))}
    </>
  );
}
